/**
 * Generates a stable, pleasant color from a user ID.
 *
 * <p>Same userId → same color across sessions and devices, so contacts
 * keep a consistent visual identity. Returns one of a curated palette
 * that matches the existing chat UI tones.
 */
const PALETTE = [
  '#E8A87C',
  '#85C1E9',
  '#A9DFBF',
  '#F1948A',
  '#C39BD3',
  '#F8C471',
  '#76D7C4',
  '#FAD7A0',
  '#D7BDE2',
  '#AED6F1',
];

const AVATAR_PALETTE = [
  { background: '#FCE7F3', border: '#F9A8D4', color: '#BE185D' },
  { background: '#DBEAFE', border: '#93C5FD', color: '#1D4ED8' },
  { background: '#DCFCE7', border: '#86EFAC', color: '#15803D' },
  { background: '#FEE2E2', border: '#FCA5A5', color: '#B91C1C' },
  { background: '#F3E8FF', border: '#D8B4FE', color: '#7E22CE' },
  { background: '#FEF3C7', border: '#FCD34D', color: '#B45309' },
  { background: '#CCFBF1', border: '#5EEAD4', color: '#0F766E' },
  { background: '#FFEDD5', border: '#FDBA74', color: '#C2410C' },
  { background: '#EDE9FE', border: '#C4B5FD', color: '#6D28D9' },
  { background: '#CFFAFE', border: '#67E8F9', color: '#0E7490' },
] as const;

export interface UserAvatarColors {
  background: string;
  border: string;
  color: string;
}

/** Deterministic color for a numeric user id. */
export function colorForUserId(userId: number | string): string {
  return PALETTE[colorIndex(userId, PALETTE.length)];
}

export function avatarColorsForUserId(userId: number | string): UserAvatarColors {
  return AVATAR_PALETTE[colorIndex(userId, AVATAR_PALETTE.length)];
}

function colorIndex(userId: number | string, paletteSize: number): number {
  const idStr = String(userId);
  let hash = 0;
  for (let i = 0; i < idStr.length; i++) {
    hash = (hash * 31 + idStr.charCodeAt(i)) >>> 0;
  }
  return hash % paletteSize;
}

/**
 * Two-letter short label for group avatars (e.g. "Backoffice Team" → "BT").
 * Strips emoji and uses the first letters of the first two words.
 */
export function shortNameFromGroupName(name: string): string {
  return initialsFromName(name);
}

export function initialsFromName(name: string, maxLength = 2): string {
  const cleaned = (name ?? '').trim();
  if (!cleaned) return '';
  return cleaned
    .split(/\s+/)
    .map(part => Array.from(part).find(character => /[\p{L}\p{N}]/u.test(character)))
    .filter(Boolean)
    .slice(0, maxLength)
    .join('')
    .toUpperCase();
}
